import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PWA 专有组件：安装引导、更新提示、离线条、权限前置说明。
 *
 * 这四个是每个 PWA 都要写、而且每次都写得不太一样的东西。它们的难点不在实现，
 * 在于**必须说对话**——说错一句，用户就装不成、不授权、或者以为应用坏了。
 */
import { useState } from 'react';
import { useAppUpdate, useInstall, useNotificationPermission, useOnline, usePwa } from '@pwaship/react';
import { useStrings } from '../i18n.js';
import { notificationErrorText } from '../permission-reason.js';
import { Button, Pressable } from './primitives.js';
import { Sheet } from './sheet.js';
import { IconAddToHome, IconBell, IconChevronDown, IconClose, IconMore, IconRefresh, IconShare, IconWifiOff } from './icons.js';
/* ─────────────────────────── 安装引导 ─────────────────────────── */
const STEP_ICONS = [IconShare, IconChevronDown, IconAddToHome, IconCheckLike];
function IconCheckLike(props) {
    return _jsx(IconAddToHome, { ...props });
}
/**
 * 分浏览器的图文步骤。
 *
 * ⚠️ 各家浏览器的菜单路径完全不同，**照着别家的说明点是找不到的**——
 * 给一套通用说明等于没给。iOS 上还要按 Safari / Chrome 系再分一次。
 */
export function InstallGuide({ browser }) {
    const { platform } = usePwa();
    const t = useStrings();
    const kind = browser ?? platform.browser;
    if (kind === 'desktop')
        return _jsx("p", { className: "ps-install-desktop", children: t.install.desktop });
    const steps = kind === 'ios-safari' ? t.install.iosSafari : kind === 'android' ? t.install.android : t.install.iosOther;
    const icons = kind === 'android' ? [IconMore, IconChevronDown, IconAddToHome] : STEP_ICONS;
    return (_jsxs("div", { className: "ps-install-guide", children: [_jsx("ol", { className: "ps-install-steps", children: steps.map((text, i) => {
                    const Icon = icons[Math.min(i, icons.length - 1)];
                    return (_jsxs("li", { className: "ps-install-step", children: [_jsx("span", { className: "ps-install-step-n", children: i + 1 }), _jsx("span", { className: "ps-install-step-icon", children: _jsx(Icon, { size: 18 }) }), _jsx("span", { className: "ps-install-step-text", children: text })] }, text));
                }) }), platform.isIOS && _jsx("p", { className: "ps-install-fallback", children: t.install.iosFallback })] }));
}
/**
 * 顶部「添加到主屏幕」提示条。
 *
 * 关键设计：那个 ✕ 不是"这次先不看"，是**「已添加，不再提示」**。
 * 因为部分安卓浏览器把"添加到桌面"做成普通快捷方式，`display-mode: standalone` 永远是
 * false，提示会反复弹、用户永远关不掉。这个按钮就是那种浏览器下的逃生口。
 */
export function InstallBanner({ text, className = '' }) {
    const install = useInstall();
    const t = useStrings();
    const [open, setOpen] = useState(false);
    if (!install.shouldPrompt)
        return null;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: `ps-install-banner ${className}`.trim(), children: [_jsxs(Pressable, { className: "ps-install-banner-main", scaleOnPress: false, onClick: () => {
                            // 能弹系统安装框就弹（安卓/桌面），弹不了就展开手动步骤（iOS）
                            if (install.canPromptNatively)
                                void install.prompt();
                            else
                                setOpen(true);
                        }, children: [_jsx(IconAddToHome, { size: 18 }), _jsx("span", { children: text ?? t.install.bannerText })] }), _jsx(Pressable, { className: "ps-install-banner-x", scaleOnPress: false, "aria-label": t.install.added, title: t.install.added, onClick: install.markDone, children: _jsx(IconClose, { size: 16 }) })] }), _jsxs(Sheet, { open: open, onClose: () => setOpen(false), title: t.install.guideTitle, children: [_jsx(InstallGuide, {}), _jsx("div", { className: "ps-install-sheet-actions", children: _jsx(Button, { variant: "secondary", block: true, onClick: install.markDone, children: t.install.added }) })] })] }));
}
/**
 * 有新版本时的提示。
 *
 * ⚠️ 那句 restartHint 必须显示出来：`skipWaiting + claim` 能缓解但**不能消除**
 * "用户看到的还是旧 Service Worker"这件事。不写这句，用户点了更新发现没变化，
 * 会认为更新功能坏了；而你自己排查时也会白花半小时查一个已经修好的 bug。
 */
export function UpdatePrompt({ className = '' }) {
    const update = useAppUpdate();
    const t = useStrings();
    const [dismissed, setDismissed] = useState(false);
    const [applying, setApplying] = useState(false);
    if (!update.available || dismissed)
        return null;
    return (_jsxs("div", { className: `ps-update ${className}`.trim(), role: "status", children: [_jsx(IconRefresh, { size: 18, className: "ps-update-icon" }), _jsxs("div", { className: "ps-update-body", children: [_jsx("p", { className: "ps-update-title", children: t.update.title }), _jsx("p", { className: "ps-update-hint", children: t.update.restartHint })] }), _jsxs("div", { className: "ps-update-actions", children: [_jsx(Pressable, { className: "ps-update-later", scaleOnPress: false, onClick: () => setDismissed(true), children: t.update.later }), _jsx(Button, { variant: "primary", size: "sm", loading: applying, onClick: () => {
                            setApplying(true);
                            void update.apply();
                        }, children: t.update.action })] })] }));
}
/* ─────────────────────────── 离线条 ─────────────────────────── */
/**
 * 离线状态条。
 *
 * ⚠️ `navigator.onLine === true` 只说明有网卡连着，**不说明能连上你的服务器**。
 * 所以这个条只用来解释"你现在看到的可能不是最新的"，不要用它来门禁任何操作。
 */
export function OfflineBar({ className = '' }) {
    const online = useOnline();
    const t = useStrings();
    if (online)
        return null;
    return (_jsxs("div", { className: `ps-offline ${className}`.trim(), role: "status", children: [_jsx(IconWifiOff, { size: 16 }), _jsx("span", { children: t.offline.banner })] }));
}
/**
 * 通知权限的**前置说明**。
 *
 * 为什么必须有这一层：浏览器只允许问一次，**被拒之后再调什么都不会发生**。
 * 所以正确顺序是先用自己的界面把"为什么需要通知"说清楚，用户同意了再调系统弹窗。
 * 直接弹系统框的应用，授权率会低得多，而且丢掉的用户再也找不回来。
 *
 * ⚠️ 系统弹窗必须在**用户点击这个按钮的那一下**里发起。这里的 onClick 是同步调 request 的，
 * 中间不能插任何 await ——插了在 iOS 上就会静默失败。
 */
export function PermissionPrimer({ open, onClose, onGranted, title, body }) {
    const permission = useNotificationPermission();
    const { platform } = usePwa();
    const t = useStrings();
    const [error, setError] = useState(null);
    // 已经不可能成功的情况，直接说清楚原因，而不是给一个点了没反应的按钮
    const blocked = permission.state === 'denied'
        ? t.notification.denied
        : !permission.support.ok && permission.support.reason === 'not-installed'
            ? t.notification.needsInstall
            : !permission.support.ok
                ? t.notification.unsupported
                : null;
    return (_jsxs(Sheet, { open: open, onClose: onClose, title: title ?? t.notification.primerTitle, children: [_jsxs("div", { className: "ps-primer", children: [_jsx("div", { className: "ps-primer-icon", children: _jsx(IconBell, { size: 30 }) }), _jsx("p", { className: "ps-primer-body", children: body ?? t.notification.primerBody }), platform.isIOS && !platform.isDesktop && _jsx("p", { className: "ps-primer-note", children: t.notification.needsInstall }), blocked && _jsx("p", { className: "ps-primer-error", children: blocked }), error && _jsx("p", { className: "ps-primer-error", children: error })] }), _jsxs("div", { className: "ps-primer-actions", children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: t.notification.primerLater }), _jsx(Button, { variant: "primary", disabled: !!blocked, onClick: () => {
                            permission.markPrimerShown();
                            // 同步发起：这一行和用户的点击之间不能有 await
                            void permission.request().then((r) => {
                                if (r.ok) {
                                    setError(null);
                                    onGranted?.();
                                    onClose();
                                }
                                else {
                                    // ⚠️ 这里**必须有兜底**。原来只认 requires-user-gesture 与 permission-denied
                                    // 两种，其余七种 FailureReason 连一条 else 都没有——用户点「开启通知」之后
                                    // 界面什么都不发生、面板还开着，正是 result.ts 顶上说的"点了没反应"。
                                    // 映射表是 Record<FailureReason, …>，core 加新原因这里不补就编译不过。
                                    setError(notificationErrorText(t, r.reason));
                                }
                            });
                        }, children: t.notification.primerAllow })] })] }));
}
/* ─────────────────────────── 图标变更提示 ─────────────────────────── */
/**
 * 换图标时必须显示的那句话。
 *
 * 系统在「添加到主屏幕」那一刻就把 name/icon 抓走并缓存了，之后改 manifest、换图片文件，
 * **桌面上那个图标纹丝不动**，iOS 和安卓都如此。这不是 bug，也没有 API 能绕过。
 * 不把这句话说出来，用户会以为功能坏了然后来报障。
 */
export function IconChangeNotice({ className = '' }) {
    const t = useStrings();
    return (_jsx("p", { className: `ps-notice ${className}`.trim(), role: "note", children: t.install.iconChangeNotice }));
}
//# sourceMappingURL=pwa.js.map